import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  User,
  Shield,
  FileText,
  Activity,
  AlertTriangle,
  Phone,
  Plus,
  Trash2,
  Pill,
} from 'lucide-react';
import PatientAllergies from '../components/PatientAllergies';
import {
  getPatient,
  getPatientVisits,
  createPatientVisit,
  getAvailableClinicians,
  updateVisit,
  deleteVisit,
  client,
  getPatientDocuments,
  getPatientDocumentUrl,
  uploadPatientDocument,
  updatePatientDocument,
  updateIntakeDocument,
  getIntakes,
  getReferralDocs,
} from '../services/api';
import PDFViewerNative from '../components/PDFViewerNative';
import { PatientMedications } from '../components/medications';
import type {
  PatientResponse,
  VisitResponse,
  AvailableClinicianResponse as AvailableClinician,
  PatientDocumentResponse,
  IntakeDocumentResponse,
} from '../services/api/types.gen';
import {
  VISITTYPEID_VALUES,
  VisitTypeId,
} from '../services/api/models/VisitTypeId';
import { useToast } from '../hooks/useToastContext';
import {
  formatDateToMMDDYYYY,
  formatDateToISO,
  calculateAge,
} from '../utils/dateUtils';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';

// Helper to format clinician display name
const formatClinicianName = (clinician: AvailableClinician): string => {
  if (clinician.clinician_first_name && clinician.clinician_last_name) {
    const credentials = clinician.clinician_credentials
      ? `, ${clinician.clinician_credentials}`
      : '';
    return `${clinician.clinician_first_name} ${clinician.clinician_last_name}${credentials}`;
  }
  return `${clinician.user_first_name} ${clinician.user_last_name}`;
};

// Small loading spinner component
const LoadingSpinner: React.FC<{ size?: 'sm' | 'md'; text?: string }> = ({
  size = 'md',
  text,
}) => {
  const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-8 w-8';
  return (
    <span className='flex items-center gap-1'>
      <div
        className={`animate-spin rounded-full border-b-2 border-primary ${sizeClass}`}
      />
      {text && <span>{text}</span>}
    </span>
  );
};

const PatientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const [patient, setPatient] = useState<PatientResponse | null>(null);
  const [visits, setVisits] = useState<VisitResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visitsError, setVisitsError] = useState<string | null>(null);
  const [clinicians, setClinicians] = useState<AvailableClinician[]>([]);
  const [patientDocuments, setPatientDocuments] = useState<
    PatientDocumentResponse[]
  >([]);
  const [intakeDocuments, setIntakeDocuments] = useState<
    IntakeDocumentResponse[]
  >([]);
  const [patientDocsLoading, setPatientDocsLoading] = useState(false);
  const [intakeDocsLoading, setIntakeDocsLoading] = useState(false);
  const [activeDocumentId, setActiveDocumentId] = useState<string>('');
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});

  // Inline editing state
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);

  // Add task dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newTaskType, setNewTaskType] = useState<VisitTypeId>(
    'RN_SKILLED_NURSING_VISIT'
  );
  const [newTaskAssignee, setNewTaskAssignee] = useState<string>('none');
  const [newTaskDate, setNewTaskDate] = useState<Date | undefined>(new Date());
  const [isCreatingTask, setIsCreatingTask] = useState(false);

  // Delete confirmation state
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const patientData = await getPatient({
          path: { patient_id: id },
          client,
        });
        if (patientData && patientData.data) {
          setPatient(patientData.data);
        }
      } catch (err) {
        console.error('Error fetching patient:', err);
        setError('Failed to load patient details');
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  useEffect(() => {
    const fetchVisits = async () => {
      if (!id) return;

      try {
        setVisitsLoading(true);
        setVisitsError(null);
        const visitsData = await getPatientVisits({
          path: { patient_id: id },
          client,
        });
        if (visitsData && visitsData.data) {
          setVisits(visitsData.data);
        }
      } catch (err) {
        console.error('Error fetching visits:', err);
        setVisitsError('Failed to load tasks');
      } finally {
        setVisitsLoading(false);
      }
    };

    fetchVisits();
  }, [id]);

  useEffect(() => {
    getAvailableClinicians({
      client,
    }).then(response => {
      if (response && response.data) {
        setClinicians(response.data);
      }
    });
  }, []);

  // Fetch patient documents
  useEffect(() => {
    if (!id) return;
    setPatientDocsLoading(true);
    getPatientDocuments({
      path: { patient_id: id },
      client,
    })
      .then(async response => {
        if (response && response.data) {
          setPatientDocuments(response.data);
          const urlMap: Record<string, string> = {};
          for (const doc of response.data) {
            try {
              const urlResponse = await getPatientDocumentUrl({
                path: { patient_id: id, document_id: doc.id },
                client,
              });
              const urlData = urlResponse?.data as { url?: string } | undefined;
              if (urlData?.url) {
                urlMap[doc.id] = urlData.url;
              }
            } catch (err) {
              console.error(`Error fetching URL for document ${doc.id}:`, err);
              showError(`There was an error fetching the document.`);
            }
          }
          setDocumentUrls(prev => ({ ...prev, ...urlMap }));
        }
      })
      .catch(err => console.error('Failed to load patient documents:', err))
      .finally(() => setPatientDocsLoading(false));
  }, [id]);

  // Fetch intake documents
  useEffect(() => {
    if (!id) return;
    setIntakeDocsLoading(true);
    getIntakes({
      query: { patient_id: id },
      client,
    })
      .then(async response => {
        if (response && response.data && response.data.items) {
          // Collect all documents from all intakes
          const allIntakeDocs: IntakeDocumentResponse[] = [];
          const urlMap: Record<string, string> = {};

          for (const intake of response.data.items) {
            if (intake.documents && intake.documents.length > 0) {
              allIntakeDocs.push(...intake.documents);

              // Fetch presigned URLs for this intake's documents
              try {
                const docsResponse = await getReferralDocs({
                  path: { intake_id: intake.id },
                  client,
                });
                if (docsResponse && docsResponse.data) {
                  // Response is { documents: [{ id: string, url: string }] }
                  const docsData = docsResponse.data as {
                    documents?: Array<{ id: string; url: string }>;
                  };
                  if (docsData.documents) {
                    for (const docUrl of docsData.documents) {
                      urlMap[docUrl.id] = docUrl.url;
                    }
                  }
                }
              } catch (err) {
                console.error(
                  `Error fetching URLs for intake ${intake.id}:`,
                  err
                );
              }
            }
          }

          setIntakeDocuments(allIntakeDocs);
          setDocumentUrls(prev => ({ ...prev, ...urlMap }));
        }
      })
      .catch(err => console.error('Failed to load intake documents:', err))
      .finally(() => setIntakeDocsLoading(false));
  }, [id]);

  // Set active document when documents are loaded
  useEffect(() => {
    const allDocs = [...intakeDocuments, ...patientDocuments];
    if (allDocs.length > 0 && !activeDocumentId) {
      setActiveDocumentId(allDocs[0].id);
    }
  }, [intakeDocuments, patientDocuments, activeDocumentId]);

  const formatDate = (dateString: string | null | undefined) => {
    return formatDateToMMDDYYYY(dateString) || 'N/A';
  };

  const getAge = (dateOfBirth: string) => {
    const age = calculateAge(dateOfBirth);
    return age !== null ? age : 'Unknown';
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'default';
      case 'scheduled':
        return 'secondary';
      case 'in_progress':
        return 'outline';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const handleTaskUpdate = async (
    taskId: string,
    updates: { assigned_clinician_id?: string | null; visit_date?: string },
    successMessage: string
  ) => {
    setUpdatingTaskId(taskId);
    try {
      const response = await updateVisit({
        path: { visit_id: taskId },
        body: updates,
        client,
      });

      if (response.data) {
        setVisits(prev =>
          prev.map(v => (v.id === taskId ? response.data! : v))
        );
        showSuccess('Task Updated', successMessage);
      }
    } catch (err) {
      console.error('Error updating task:', err);
      showError('Update Failed', 'Failed to update task.');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleAssigneeChange = (taskId: string, clinicianId: string) => {
    handleTaskUpdate(
      taskId,
      { assigned_clinician_id: clinicianId === 'none' ? null : clinicianId },
      'Assignee updated successfully.'
    );
  };

  const handleDateChange = (taskId: string, newDate: Date | undefined) => {
    if (!newDate) return;
    const formattedDate = formatDateToISO(newDate);
    if (!formattedDate) return;
    handleTaskUpdate(
      taskId,
      { visit_date: formattedDate },
      'Target date updated successfully.'
    );
  };

  const handleCreateTask = async () => {
    if (!id) return;
    if (!newTaskDate) {
      showError('Validation Error', 'Please select a target date.');
      return;
    }

    setIsCreatingTask(true);
    try {
      const response = await createPatientVisit({
        path: { patient_id: id },
        body: {
          visit_type: newTaskType,
          visit_date: formatDateToISO(newTaskDate),
          assigned_clinician_id:
            newTaskAssignee === 'none' ? null : newTaskAssignee,
        },
        client,
      });

      if (response.data) {
        setVisits(prev => [...prev, response.data!]);
        setIsAddDialogOpen(false);
        setNewTaskType('RN_SKILLED_NURSING_VISIT');
        setNewTaskAssignee('none');
        setNewTaskDate(new Date());
        showSuccess('Task Created', 'New task added successfully.');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      showError('Create Failed', 'Failed to create task.');
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskId) return;

    setIsDeletingTask(true);
    try {
      await deleteVisit({
        path: { visit_id: deleteTaskId },
        client,
      });

      setVisits(prev => prev.filter(v => v.id !== deleteTaskId));
      setDeleteTaskId(null);
      showSuccess('Task Deleted', 'Task deleted successfully.');
    } catch (err) {
      console.error('Error deleting task:', err);
      showError('Delete Failed', 'Failed to delete task.');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const handleDocumentRename = async (documentId: string, fileName: string) => {
    if (!id) return;

    // Check if this is a patient document or an intake document
    const isPatientDoc = patientDocuments.some(d => d.id === documentId);

    try {
      if (isPatientDoc) {
        await updatePatientDocument({
          path: { patient_id: id, document_id: documentId },
          body: { file_name: fileName },
          client,
        });
        setPatientDocuments(prev =>
          prev.map(doc =>
            doc.id === documentId ? { ...doc, file_name: fileName } : doc
          )
        );
      } else {
        await updateIntakeDocument({
          path: { document_id: documentId },
          body: { file_name: fileName },
          client,
        });
        setIntakeDocuments(prev =>
          prev.map(doc =>
            doc.id === documentId ? { ...doc, file_name: fileName } : doc
          )
        );
      }
      showSuccess('Document Renamed', 'Document renamed successfully.');
    } catch (err) {
      console.error('Error renaming document:', err);
      showError('Rename Failed', 'Failed to rename document.');
    }
  };

  const handleDocumentAdd = async (file: File, displayName: string) => {
    if (!id) return null;

    try {
      const response = await uploadPatientDocument({
        path: { patient_id: id },
        query: { display_name: displayName },
        body: { file },
        client,
      });

      if (response.data) {
        setPatientDocuments(prev => [...prev, response.data!]);

        try {
          const urlResponse = await getPatientDocumentUrl({
            path: { patient_id: id, document_id: response.data.id },
            client,
          });
          const urlData = urlResponse?.data as { url?: string } | undefined;
          if (urlData?.url) {
            const url = urlData.url;
            setDocumentUrls(prev => ({
              ...prev,
              [response.data!.id]: url,
            }));
          }
        } catch (urlErr) {
          console.error('Error fetching URL for new document:', urlErr);
        }

        return {
          id: response.data.id,
          fileName: response.data.file_name,
        };
      }
      return null;
    } catch (err) {
      console.error('Error uploading document:', err);
      showError('Upload Failed', 'Failed to upload document.');
      return null;
    }
  };

  const sortedTasks = useMemo(() => {
    return [...visits].sort((a, b) => {
      const dateA = new Date(a.visit_date);
      const dateB = new Date(b.visit_date);
      return dateA.getTime() - dateB.getTime();
    });
  }, [visits]);

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='flex justify-center mb-4'>
            <LoadingSpinner size='md' />
          </div>
          <p className='text-muted-foreground'>Loading patient details...</p>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Patient</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Patient not found'}
          </p>
          <Button onClick={() => navigate('/patients')} variant='outline'>
            <ArrowLeft className='h-4 w-4' />
            Back to Patients
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      {/* Patient Overview Card */}
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <div className='flex-1'>
              <CardTitle className='text-xl'>
                {`${patient.first_name} ${patient.last_name}`}
              </CardTitle>
              <CardDescription>
                MRN: {patient.ssn || 'N/A'} • Age:{' '}
                {getAge(patient.date_of_birth)} years
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2'>
            <div className='flex items-center gap-2'>
              <Calendar className='h-4 w-4 text-muted-foreground' />
              <span className='text-sm'>
                DOB: {formatDate(patient.date_of_birth)}
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <Phone className='h-4 w-4 text-muted-foreground' />
              <span className='text-sm'>{patient.phone || 'N/A'}</span>
            </div>
            <div className='flex items-center gap-2'>
              <MapPin className='h-4 w-4 text-muted-foreground' />
              <span className='text-sm'>
                {patient.address || 'N/A'}
                {(patient.city || patient.state) && ', '}
                {[patient.city, patient.state].filter(Boolean).join(', ')}
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <Shield className='h-4 w-4 text-muted-foreground' />
              <span className='text-sm'>
                {patient.insurance_name || 'No insurance'}
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <User className='h-4 w-4 text-muted-foreground' />
              <span className='text-sm'>
                {patient.branch_name || 'No branch'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Visits and Documents */}
      <Tabs defaultValue='visits' className='space-y-3'>
        <TabsList className='w-full'>
          <TabsTrigger value='visits' className='flex-1'>
            Visits
          </TabsTrigger>
          <TabsTrigger value='documents' className='flex-1'>
            Documents
            {(intakeDocuments.length > 0 || patientDocuments.length > 0) && (
              <Badge variant='secondary' className='ml-1.5 text-xs px-1.5 py-0'>
                {intakeDocuments.length + patientDocuments.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value='allergies' className='flex-1'>
            Allergies
          </TabsTrigger>
          <TabsTrigger value='medications' className='flex-1'>
            <Pill className='h-4 w-4 mr-1.5' />
            Medications
          </TabsTrigger>
        </TabsList>

        <TabsContent value='visits'>
          <Card>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <div>
                  <CardTitle className='flex items-center gap-2'>
                    <Activity className='h-5 w-5' />
                    Tasks
                  </CardTitle>
                  <CardDescription>
                    {sortedTasks.length > 0 &&
                    sortedTasks[0].episode_start_date ? (
                      <>
                        Episode: {formatDate(sortedTasks[0].episode_start_date)}{' '}
                        - {formatDate(sortedTasks[0].episode_end_date || '')}
                      </>
                    ) : (
                      'Episode tasks and assignments'
                    )}
                  </CardDescription>
                </div>
                <Button size='sm' onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className='h-4 w-4' />
                  Add Task
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div>
                {visitsLoading ? (
                  <div className='text-center py-8'>
                    <div className='flex justify-center mb-4'>
                      <LoadingSpinner size='md' />
                    </div>
                    <p className='text-muted-foreground'>Loading tasks...</p>
                  </div>
                ) : visitsError ? (
                  <div className='text-center py-8 text-muted-foreground'>
                    <AlertTriangle className='h-12 w-12 mx-auto mb-4 opacity-50' />
                    <p>{visitsError}</p>
                  </div>
                ) : sortedTasks.length === 0 ? (
                  <div className='text-center py-8 text-muted-foreground'>
                    <Activity className='h-12 w-12 mx-auto mb-4 opacity-50' />
                    <p>No tasks found for this patient</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task Type</TableHead>
                        <TableHead>Assignee</TableHead>
                        <TableHead>Target Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className='w-[60px]'></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedTasks.map(task => (
                        <TableRow
                          key={task.id}
                          className='cursor-pointer hover:bg-muted/50'
                          onClick={() =>
                            navigate(`/patients/${id}/visits/${task.id}`)
                          }
                        >
                          <TableCell>
                            <Badge variant='outline' className='text-xs'>
                              {task.visit_type}
                            </Badge>
                          </TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <Select
                              value={task.assigned_clinician_id || 'none'}
                              onValueChange={value =>
                                handleAssigneeChange(task.id, value)
                              }
                              disabled={updatingTaskId === task.id}
                            >
                              <SelectTrigger
                                selectSize='sm'
                                className='w-[200px]'
                              >
                                <SelectValue placeholder='Select assignee' />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value='none'>Unassigned</SelectItem>
                                {clinicians.map(clinician => (
                                  <SelectItem
                                    key={clinician.id}
                                    value={clinician.id}
                                  >
                                    {formatClinicianName(clinician)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <DatePicker
                              date={
                                task.visit_date
                                  ? new Date(task.visit_date)
                                  : undefined
                              }
                              onDateChange={(date: Date | undefined) =>
                                handleDateChange(task.id, date)
                              }
                              disabled={updatingTaskId === task.id}
                              datePickerSize='sm'
                            />
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={getStatusBadgeVariant(task.status)}
                              className='text-xs'
                            >
                              {task.status}
                            </Badge>
                          </TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => setDeleteTaskId(task.id)}
                              disabled={updatingTaskId === task.id}
                            >
                              <Trash2 className='h-4 w-4 text-destructive' />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='allergies' className='space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
            <PatientAllergies patient={patient} />
          </div>
        </TabsContent>

        <TabsContent value='documents'>
          <Card>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2 text-sm font-medium'>
                  <FileText className='h-5 w-5' />
                  <CardTitle>Documents</CardTitle>
                  {(intakeDocuments.length > 0 ||
                    patientDocuments.length > 0) && (
                    <Badge variant='secondary' className='text-xs'>
                      {intakeDocuments.length + patientDocuments.length}
                    </Badge>
                  )}
                </div>
                <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                  {intakeDocsLoading && (
                    <LoadingSpinner size='sm' text='Loading...' />
                  )}
                  {patientDocsLoading && (
                    <LoadingSpinner size='sm' text='Loading...' />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className='p-0'>
              <div className='h-[600px]'>
                <PDFViewerNative
                  documents={[
                    // Intake documents first (from referral)
                    ...intakeDocuments.map(doc => ({
                      id: doc.id,
                      fileName: doc.file_name,
                      tooltipText: `[Intake] ${doc.file_name}`,
                      isPersisted: true,
                      usedInExtraction: doc.used_in_extraction ?? true,
                    })),
                    // Then patient documents
                    ...patientDocuments.map(doc => ({
                      id: doc.id,
                      fileName: doc.file_name,
                      isPersisted: true,
                      usedInExtraction: true,
                    })),
                  ]}
                  documentUrls={documentUrls}
                  activeDocumentId={activeDocumentId}
                  onActiveDocumentChange={setActiveDocumentId}
                  onDocumentRename={handleDocumentRename}
                  onDocumentAdd={handleDocumentAdd}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='medications'>
          <PatientMedications patientId={id!} />
        </TabsContent>
      </Tabs>

      {/* Add Task Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Task</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            <div>
              <label className='text-sm font-medium'>Task Type</label>
              <Select
                value={newTaskType}
                onValueChange={v => setNewTaskType(v as VisitTypeId)}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder='Select task type' />
                </SelectTrigger>
                <SelectContent>
                  {VISITTYPEID_VALUES.map(type => (
                    <SelectItem key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className='text-sm font-medium'>Assignee</label>
              <Select
                value={newTaskAssignee}
                onValueChange={setNewTaskAssignee}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder='Select assignee' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='none'>Unassigned</SelectItem>
                  {clinicians.map(clinician => (
                    <SelectItem key={clinician.id} value={clinician.id}>
                      {formatClinicianName(clinician)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className='text-sm font-medium'>Target Date</label>
              <div className='mt-1'>
                <DatePicker date={newTaskDate} onDateChange={setNewTaskDate} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setIsAddDialogOpen(false)}
              disabled={isCreatingTask}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateTask} disabled={isCreatingTask}>
              {isCreatingTask ? 'Creating...' : 'Add Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTaskId}
        onOpenChange={open => !open && setDeleteTaskId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>
            Are you sure you want to delete this task? This action cannot be
            undone.
          </p>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteTaskId(null)}
              disabled={isDeletingTask}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteTask}
              disabled={isDeletingTask}
            >
              {isDeletingTask ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientDetail;
